import React from 'react';
import LegalPageLayout from './LegalPageLayout';

const PrivacyPolicyPage: React.FC = () => (
  <LegalPageLayout title="Privacy Policy" lastUpdated="June 4, 2026">
    <h2>1. Introduction</h2>
    <p>
      SkipScreen (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) respects your privacy. This
      Privacy Policy explains how we collect, use, store, and share personal information when you
      use our website, applications, and related services (the &quot;Service&quot;). By using
      SkipScreen, you agree to the practices described here.
    </p>

    <h2>2. Information we collect</h2>
    <h3>Information you provide</h3>
    <p>Candidates may provide:</p>
    <ul>
      <li>Name, email, phone, and account credentials</li>
      <li>Profile details, resume, skills, and work preferences</li>
      <li>Video and written screening responses</li>
      <li>Portfolio links, photos, and supporting documents</li>
      <li>Reference contact information you choose to add</li>
    </ul>
    <p>Recruiters may provide name, work email, company, and job-related data.</p>

    <h3>Information collected automatically</h3>
    <ul>
      <li>Usage data (pages viewed, features used, interactions)</li>
      <li>Device and browser data (IP address, device type, operating system)</li>
      <li>Security and authentication logs</li>
      <li>Analytics data to improve performance and product design</li>
    </ul>

    <h2>3. How we use information</h2>
    <p>We use personal information to:</p>
    <ul>
      <li>Create and manage your account</li>
      <li>Display candidate profiles to authorized recruiters and employers</li>
      <li>Facilitate messaging, scheduling, and reference verification</li>
      <li>Improve prompts, recommendations, and platform features</li>
      <li>Send service, security, and transactional communications</li>
      <li>Prevent fraud, abuse, and unauthorized access</li>
      <li>Comply with legal obligations</li>
    </ul>
    <p>
      <strong>SkipScreen does not sell your personal data.</strong>
    </p>

    <h2>4. How we share information</h2>
    <p>We may share information:</p>
    <ul>
      <li>With recruiters and employers you authorize or who access your shared profile link</li>
      <li>
        With service providers that help us host, email, analyze, store media, or operate the
        platform (under contractual confidentiality obligations)
      </li>
      <li>With references you invite to complete verification feedback</li>
      <li>When required by law, court order, or to protect rights and safety</li>
      <li>In connection with a merger, acquisition, or asset sale (with appropriate safeguards)</li>
    </ul>

    <h2>5. Artificial intelligence</h2>
    <p>
      SkipScreen may use AI to generate role-specific prompts, assist with profile content, and help
      recruiters summarize candidate information. AI features process information you voluntarily
      provide in the Service. We may use anonymized or aggregated data to improve product quality.
      Google user data obtained through integrations is not used to train generalized AI models
      outside the purposes disclosed for that integration.
    </p>

    <h2>6. Google Calendar integration</h2>
    <p>
      If you connect Google Calendar, we access calendar permissions only to create and manage
      interview events you initiate in SkipScreen. We do not sell Google user data and use it only
      to provide the scheduling features you request.
    </p>

    <h2>7. Data retention</h2>
    <p>
      We retain information while your account is active and as needed to provide the Service,
      resolve disputes, enforce agreements, and meet legal requirements. You may request deletion of
      your account through settings or support; some records may be retained where legally required
      or anonymized for analytics.
    </p>

    <h2>8. Security</h2>
    <p>
      We use administrative, technical, and organizational measures designed to protect personal
      information, including encryption in transit and access controls. No method of transmission or
      storage is completely secure.
    </p>

    <h2>9. Your choices and rights</h2>
    <p>Depending on your location, you may have the right to:</p>
    <ul>
      <li>Access, correct, or delete personal information</li>
      <li>Object to or restrict certain processing</li>
      <li>Withdraw consent where processing is consent-based</li>
      <li>Request a portable copy of your data</li>
    </ul>
    <p>
      Use in-app privacy settings or contact us to exercise these rights. We will respond within
      applicable legal timeframes.
    </p>

    <h2>10. International users</h2>
    <p>
      If you access the Service from outside the United States, your information may be processed in
      the United States or other countries where we or our providers operate, which may have
      different data protection laws.
    </p>

    <h2>11. Children</h2>
    <p>
      SkipScreen is not intended for users under 18. We do not knowingly collect personal
      information from children.
    </p>

    <h2>12. Changes to this policy</h2>
    <p>
      We may update this Privacy Policy periodically. The &quot;Last updated&quot; date at the top
      reflects the latest version. Material changes will be posted on this page.
    </p>

    <div className="legal-page__contact">
      <h2>13. Contact</h2>
      <p>
        Privacy questions: <a href="mailto:privacy@useskipscreen.com">privacy@useskipscreen.com</a>
        <br />
        General support: <a href="mailto:support@useskipscreen.com">support@useskipscreen.com</a>
      </p>
    </div>
  </LegalPageLayout>
);

export default PrivacyPolicyPage;
